import { useState, useCallback, useRef, useEffect, memo, ReactNode } from 'react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector } from 'react-redux';
import { promptForHubUpdate } from '@client/utils/prompt-for-hub-update';
import { setDatadogUser } from '@client/utils/datadog';
import {
  AppStateContext,
  appContext,
  appStateContext,
  appActionContext,
  appUIStateContext,
  appUIActionContext,
  appErrorStateContext,
  appErrorActionContext,
} from './contexts';
import { isValidHUBDashobardUser, clearAuthTokens, setAuth0Token } from '../../utils/auth';
import { retrieveAllRegions } from '../../state/resources/regions/actions';
import { retrieveAllShipperPreferences } from '../../state/resources/shipperPreferences/actions';
import { fetchUserInfoAction } from '../../_blessed/store/slices/authSlice';
import { useAppDispatch } from '../../_blessed/hooks/useAppDispatch';
import {
  selectAuthUser,
  selectAuthUserDepartment,
  selectAuthUserLoading,
} from '../../_blessed/store/selectors/authSliceSelectors';

const AppProviderMemo = memo(
  (props: any) =>
    // eslint-disable-next-line react/prop-types
    props.children,
);

/**
 * Controls how often the app will check for updates in milliseconds.
 */
const POLLING_PERIOD_MS = 30000;

export default function AppProvider(props: { children: ReactNode }) {
  const appDispatch = useAppDispatch();
  const {
    isLoading: auth0IsLoading,
    isAuthenticated: auth0IsAuthenticated,
    user: auth0User,
    loginWithRedirect,
    getAccessTokenSilently,
    logout,
  } = useAuth0();

  const [state, setState] = useState<AppStateContext>({
    'ui.leftSidebar.open': false,
    'ui.leftSidebarForArAp.open': false,
    'ui.leftSidebar.component': null,
  });

  const ldClient = useLDClient();

  const loadingAuthUser = useSelector(selectAuthUserLoading);
  const authUser = useSelector(selectAuthUser);
  const selectedDepartment = useSelector(selectAuthUserDepartment);

  if (!loadingAuthUser && selectedDepartment && !isValidHUBDashobardUser(selectedDepartment)) {
    clearAuthTokens();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  const [appUIState, setAppUIState] = useState({
    'ui.rightSidebar.open': false,
  });
  const [appErrorState, setAppErrorState] = useState({});

  useEffect(() => {
    if (auth0IsLoading || auth0IsAuthenticated) {
      return;
    }

    (async () => {
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname + window.location.search },
      });
    })();
  }, [auth0IsLoading, auth0IsAuthenticated, loginWithRedirect]);

  useEffect(() => {
    if (auth0IsLoading || !auth0IsAuthenticated) {
      return;
    }

    getAccessTokenSilently()
      .then((token) => {
        setAuth0Token(token);
      })
      .then(() => {
        appDispatch(fetchUserInfoAction());
      })
      .catch((error) => {
        console.error('Error getting access token:', error);
      });
  }, [auth0IsLoading, auth0IsAuthenticated, auth0User]);

  const executeAppAction = useCallback(
    (action: any) =>
      action(setState, {
        setAppErrorState,
      }),
    [],
  );

  const executeAppUIAction = useCallback((action: any) => action(setAppUIState), []);

  const executeAppErrorAction = useCallback((action: any) => action(setAppErrorState), []);

  const appContextValueRef = useRef({ state, executeAction: executeAppAction });

  if (state !== appContextValueRef.current.state) {
    appContextValueRef.current = {
      state,
      executeAction: executeAppAction,
    };
  }

  useEffect(() => {
    const identifyUser = async () => {
      if (!auth0IsLoading && authUser) {
        setDatadogUser({
          id: authUser.uuid,
          email: authUser.email1,
          name: `${authUser.firstName} ${authUser.lastName}`,
        });
      }

      if (ldClient && authUser) {
        try {
          await ldClient.identify({
            key: authUser.uuid,
            firstName: authUser.firstName,
            lastName: authUser.lastName,
            email: authUser.email1,
          });
        } catch (error) {
          console.error('Error identifying LaunchDarkly user:', error);
        }
      }
    };

    identifyUser();
    if (authUser) {
      executeAppAction(retrieveAllRegions());
      executeAppAction(retrieveAllShipperPreferences());
    }
  }, [auth0IsLoading, authUser, ldClient]);

  useEffect(() => {
    const intervalId = window.setInterval(promptForHubUpdate, POLLING_PERIOD_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [promptForHubUpdate]);

  useEffect(() => {
    if (!loadingAuthUser && !authUser) {
      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      });
    }
  }, [loadingAuthUser, authUser]);

  return auth0IsAuthenticated && !loadingAuthUser && authUser ? (
    <appContext.Provider value={appContextValueRef.current}>
      <appStateContext.Provider value={state}>
        <appActionContext.Provider value={executeAppAction}>
          <appUIStateContext.Provider value={appUIState}>
            <appUIActionContext.Provider value={executeAppUIAction}>
              <appErrorStateContext.Provider value={appErrorState}>
                <appErrorActionContext.Provider value={executeAppErrorAction}>
                  <AppProviderMemo>{props.children}</AppProviderMemo>
                </appErrorActionContext.Provider>
              </appErrorStateContext.Provider>
            </appUIActionContext.Provider>
          </appUIStateContext.Provider>
        </appActionContext.Provider>
      </appStateContext.Provider>
    </appContext.Provider>
  ) : null;
}
